import { checkboxClasses, linkClasses, radioClasses, sliderClasses, type Components } from '@mui/material';
import { darkBlueChannel } from '../colors';
import { cssBaseline } from '../cssBaseline';
import { themeVars } from '../themeVars';
import * as accordion from './accordion';
import * as alert from './alert';
import * as buttons from './buttons';
import * as card from './card';
import * as chips from './chips';
import * as datePickers from './datePickers';
import * as dialogs from './dialogs';
import * as dropdowns from './dropdowns';
import * as inputs from './inputs';
import * as list from './list';
import * as pagination from './pagination';
import * as stepper from './stepper';
import * as svgIcon from './svgIcon';
import * as switches from './switches';
import * as table from './table';
import * as tabs from './tabs';
import * as tooltip from './tooltip';
import { paletteColorNames, type Theme } from './utils';

/**
 * To override default props, see ./defaultProps
 */
export const styleOverrides: Components<Theme> = {
  ...accordion,
  ...alert,
  ...buttons,
  ...card,
  ...chips,
  ...datePickers,
  ...dialogs,
  ...dropdowns,
  ...inputs,
  ...list,
  ...pagination,
  ...stepper,
  ...svgIcon,
  ...switches,
  ...table,
  ...tabs,
  ...tooltip,

  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: themeVars(theme).palette.info.main,
        color: themeVars(theme).palette.info.contrastText,
        fontSize: theme.typography.pxToRem(16),
      }),
    },
  },
  MuiBackdrop: {
    variants: [
      {
        props: { invisible: false },
        style: ({ theme }) => ({
          backgroundColor: `rgba(${darkBlueChannel[90]} / 0.6)`,
          ...theme.applyStyles('dark', {
            backgroundColor: `rgba(0 0 0 / 0.6)`,
          }),
          backdropFilter: 'blur(6px)',
        }),
      },
    ],
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      separator: ({ theme }) => ({
        color: themeVars(theme).palette.text.secondary,
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 0,
        boxSizing: 'content-box',
        borderRadius: theme.shape.borderRadius,
        flexShrink: 0,
        '--mds-Checkbox-ringColor': themeVars(theme).palette.text.secondary,
        [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
          '--mds-Checkbox-ringStyle': 'none',
          '--mds-Checkbox-fillColor': themeVars(theme).palette.action.selectedContrast,
        },
        [`&.${checkboxClasses.disabled}`]: {
          '--mds-Checkbox-ringColor': themeVars(theme).palette.action.disabled,
          [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
            '--mds-Checkbox-fillColor': themeVars(theme).palette.action.disabled,
          },
        },
        '&.Mui-focusVisible': {
          boxShadow: `0 0 0 1px ${themeVars(theme).palette.background.default}, 0 0 0 calc(3px) ${themeVars(theme).palette.border.focused}`,
        },
      }),
    },
    variants: [
      {
        props: { size: 'small' as const },
        style: {
          width: 16,
          height: 16,
        },
      },
      {
        props: { size: 'medium' as const },
        style: {
          width: 18,
          height: 18,
        },
      },
    ],
  },
  MuiCssBaseline: {
    styleOverrides: cssBaseline,
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: `rgba(${themeVars(theme).palette.common.black} / 0.16)`,
      }),
      bar1: ({ theme }) => ({
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: themeVars(theme).palette.border.focused,
      }),
    },
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&': {
          textUnderlineOffset: '0.15em',
        },
        [`&.${linkClasses.focusVisible}`]: {
          outline: 'none',
          position: 'relative',
          '&:after': {
            content: '""',
            pointerEvents: 'none',
            position: 'absolute',
            left: -4,
            top: -2,
            width: 'calc(100% + 8px)',
            height: 'calc(100% + 4px)',
            border: '1.5px solid',
            borderColor: themeVars(theme).palette.border.focused,
            borderRadius: theme.shape.borderRadius,
          },
        },
      }),
    },
    variants: [
      ...paletteColorNames.map((color) => ({
        props: { color },
        style: ({ theme }: { theme: Theme }) => ({
          color: themeVars(theme).palette[color].text,
          textDecorationColor: 'currentColor',
        }),
      })),
      // Override 'info' with the default link color (at the time of writing, it wasn't
      // possible to extend Link's color prop)
      {
        props: { color: 'info' },
        style: ({ theme }) => ({
          color: themeVars(theme).palette.link.default,
          textDecorationColor: 'currentColor',
          '&:active': {
            color: themeVars(theme).palette.link.pressed,
            textDecorationColor: 'currentColor',
          },
        }),
      },
    ],
  },
  MuiPaper: {
    variants: [
      {
        props: ({ ownerState }) => !ownerState.square,
        style: {
          borderRadius: 'unset',
        },
      },
      {
        props: {
          variant: 'outlined',
        },
        style: {
          border: 'unset',
        },
      },
      {
        props: {
          variant: 'elevation',
        },
        style: {
          boxShadow: 'unset',
          backgroundImage: 'unset',
        },
      },
    ],
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&.MuiRadio-colorPrimary': {
          // Radio color is also modified by MuiFormControlLabel styles
          color: themeVars(theme).palette.text.secondary,
        },
        [`&.MuiRadio-colorPrimary.${radioClasses.checked}`]: {
          color: themeVars(theme).palette.action.selectedContrast,
        },
        [`&.${radioClasses.disabled}`]: {
          color: themeVars(theme).palette.action.disabled,
        },
        [`&.Mui-focusVisible`]: {
          boxShadow: `0 0 0 0 ${themeVars(theme).palette.background.default}, 0 0 0 2px ${themeVars(theme).palette.border.focused}`,
        },
      }),
    },
  },
  MuiSlider: {
    styleOverrides: {
      mark: ({ theme }) => ({
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: themeVars(theme).palette.primary.main,
        opacity: 1,
        transform: 'translate(-2px, -2px)',
        ...theme.applyStyles('dark', {
          backgroundColor: themeVars(theme).palette.text.primary,
        }),

        [`&.${sliderClasses.markActive}`]: {
          backgroundColor: themeVars(theme).palette.primary.contrastText,
          opacity: 1,
        },

        '&[data-index="0"]': {
          backgroundColor: themeVars(theme).palette.primary.main,
          ...theme.applyStyles('dark', {
            backgroundColor: themeVars(theme).palette.text.primary,
          }),
        },
      }),
      thumb: ({ theme }) => ({
        width: 14,
        height: 14,
        [`&, &.${sliderClasses.dragging} .${sliderClasses.thumb}`]: {
          transition: theme.transitions.create(['width', 'height'], {
            duration: theme.transitions.duration.short,
          }),
        },
        '&::before': {
          boxShadow: 'none',
        },
        // TODO Not actually triggered on :focus-visible
        [`&.${sliderClasses.focusVisible}`]: {
          boxShadow: `0 0 0 1px ${themeVars(theme).palette.background.default}, 0 0 0 3px ${themeVars(theme).palette.border.focused}`,
        },
        '&:hover': {
          boxShadow: 'none',
          width: 18,
          height: 18,
        },
      }),
    },
  },
  MuiToolbar: {
    variants: [
      {
        props: ({ ownerState }) => !ownerState.disableGutters,
        style: ({ theme }) => theme.mixins.gutters,
      },
    ],
  },
  MuiTypography: {
    variants: paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }) => ({
        // MUI used palette[color].main
        color: themeVars(theme).palette[color].text,
      }),
    })),
  },
};
